import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveSession } from '../../api/session.ts';
import { realtimeClient } from '../../api/realtime.ts';
import type { Session } from '../../api/types.ts';
import { applySessionUpdate, type SessionUpdatePayload } from './sessionInfo.ts';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { useT } from '../../i18n/i18n.tsx';

/**
 * The live session record both `StageRoot` (join QR/URL, marquee default text, stage message)
 * and `OperatorConsole` (venue/session name, request-mode display) read. One `GET` on mount, then
 * every `session.updated` realtime frame (the session lifecycle hook publishes these on the
 * `settings` channel — see that file's own doc comment) merges in over the existing
 * `realtimeClient` connection this window already has, no new SSE subscription needed.
 */
export function useSessionInfo() {
  const t = useT();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // MVP-344: `error` is rendered verbatim — `TonightSettings.tsx`'s `ErrorState`, and
  // `SessionStartPanel.tsx`'s `startError ?? error`. See `api/errorMessage.ts` for why a caught
  // value never reaches either one directly. `refresh` keeps `t` in its deps so the sentence is in
  // whichever language is current when the fetch fails, not whichever was current on mount.
  const refresh = useCallback(async () => {
    try {
      const fresh = await getActiveSession();
      setSession(fresh);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, t('common.serverUnreachableRetry')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // P2 fix (round 5): same StrictMode double-invoke as useRotation.ts's own matching comment —
  // `GET /api/sessions` was confirmed live firing twice per mount (main.tsx's `<StrictMode>`
  // replays this effect once with no cleanup). No per-call identity to key on here (unlike
  // useRotation's `sessionId`), so a plain "already ran" ref stands in — `refresh` itself stays
  // freely callable afterwards (the retry/realtime paths still call it directly, not through
  // this effect).
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return realtimeClient.onFrame((frame) => {
      // MVP-483: a `hello` frame is the stream's own signal that it just (re)connected — one frame
      // per successful `connectOnce()`. Every `session.updated` published while the stream was down
      // is simply gone (the hub does not replay), so a reconnect with no refetch leaves this hook on
      // whatever the session looked like before the drop: a rotated join code, a changed request
      // mode, last night's marquee text. `OperatorConsole.tsx` and `RequestPage.tsx` have reacted to
      // this frame since R9-P1-D; this hook, `useRotationState` and `usePlaybackState` had not.
      if (frame.event === 'hello') {
        void refresh();
        return;
      }
      if (frame.event !== 'session.updated') return;
      setSession((current) => applySessionUpdate(current, frame.payload as SessionUpdatePayload));
    });
  }, [refresh]);

  // MVP-218 fix: lets a caller that already has the fresh, authoritative fields from its own
  // write (e.g. `PATCH /api/session/current`'s response) apply them immediately instead of
  // firing a second `GET /api/sessions` (and racing rapid successive edits against it).
  const applySession = useCallback((partial: Partial<Session>) => {
    setSession((current) => (current ? { ...current, ...partial } : current));
  }, []);

  return { session, loading, error, refresh, applySession };
}
